import { useState, useRef, useCallback, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { ALGORITHMS, DEFAULT_ALGORITHMS, hashesMatch, algorithmForHashLength } from '../utils/hashFile';
import { buildHashLogText, buildHashReportDoc, fmtBytes } from '../utils/hashReport';
import { MAX_FILE_BYTES } from '../utils/audioCapacity';
import { download } from '../utils/transcriptExports';
import createHashWorker from '../workers/createHashWorker';

// ── helpers ──────────────────────────────────────────────────────────────────

const ACCEPT = undefined; // deliberately any file type — evidence isn't limited to media

const smallButton = {
  background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
  padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600,
};

const STATUS_STYLES = {
  queued: { label: 'Queued', color: '#64748b', bg: '#f1f5f9' },
  hashing: { label: 'Hashing', color: '#4338ca', bg: '#e0e7ff' },
  done: { label: 'Done', color: '#15803d', bg: '#dcfce7' },
  error: { label: 'Failed', color: '#b91c1c', bg: '#fee2e2' },
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.queued;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: s.color, background: s.bg,
      padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function Bar({ pct }) {
  return (
    <div style={{ height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`,
        background: '#6366f1', borderRadius: 999, transition: 'width .2s ease',
      }} />
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function HashVerifier() {
  const [jobs, setJobs] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [sizeError, setSizeError] = useState('');
  const [algorithms, setAlgorithms] = useState(DEFAULT_ALGORITHMS);

  const [running, setRunning] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [hashProg, setHashProg] = useState(0);
  const [copied, setCopied] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const jobsRef = useRef([]);
  const workerRef = useRef(null);
  const runningRef = useRef(false);
  const cancelRef = useRef(false);
  const rejectRef = useRef(null);
  const inputRef = useRef(null);
  const nextIdRef = useRef(1);

  jobsRef.current = jobs;

  const doneJobs = jobs.filter(j => j.status === 'done');
  const queuedCount = jobs.filter(j => j.status === 'queued').length;

  const updateJob = useCallback((id, patch) => {
    setJobs(prev => prev.map(j => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  useEffect(() => () => { workerRef.current?.terminate(); }, []);

  const toggleAlgorithm = (id) => {
    if (running) return;
    setAlgorithms(prev => (prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]));
  };

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const tooBig = incoming.filter(f => f.size > MAX_FILE_BYTES);
    setSizeError(tooBig.length
      ? `${tooBig.map(f => f.name).join(', ')} — over ${fmtBytes(MAX_FILE_BYTES)}. `
        + 'A file that large cannot be read into browser memory at all.'
      : '');

    const sig = f => `${f.name}|${f.size}`;
    const have = new Set(jobsRef.current.map(j => sig(j.file)));
    const added = incoming
      .filter(f => f.size <= MAX_FILE_BYTES && !have.has(sig(f)))
      .map(f => ({
        id: nextIdRef.current++,
        file: f,
        name: f.name,
        size: f.size,
        status: 'queued',
        hashes: {},
        expectedHash: '',
        error: '',
      }));

    if (!added.length) return;
    setJobs(prev => [...prev, ...added]);
  }, []);

  const removeJob = useCallback((id) => {
    setJobs(prev => prev.filter(j => j.id !== id));
  }, []);

  const clearAll = useCallback(() => setJobs([]), []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const setExpectedHash = useCallback((id, value) => {
    updateJob(id, { expectedHash: value });
  }, [updateJob]);

  const hashOne = useCallback((worker, bytes, jobId) => (
    new Promise((resolve, reject) => {
      rejectRef.current = reject;

      const onMessage = (e) => {
        const msg = e.data;
        if (msg.id !== jobId) return;
        if (msg.type === 'progress') {
          setHashProg(msg.fraction);
        } else if (msg.type === 'done') {
          worker.removeEventListener('message', onMessage);
          rejectRef.current = null;
          resolve(msg.results);
        } else if (msg.type === 'error') {
          worker.removeEventListener('message', onMessage);
          rejectRef.current = null;
          reject(new Error(msg.message));
        }
      };
      worker.addEventListener('message', onMessage);

      const buf = bytes.buffer;
      worker.postMessage({ id: jobId, bytes: buf, algorithms }, [buf]);
    })
  ), [algorithms]);

  const runQueue = useCallback(async () => {
    if (runningRef.current) return;
    if (!jobsRef.current.some(j => j.status === 'queued')) return;
    if (!algorithms.length) return;

    runningRef.current = true;
    cancelRef.current = false;
    setRunning(true);
    trackToolAction('/hashverifier', 'File Hash / Chain-of-Custody Verifier');

    let worker = workerRef.current;
    if (!worker) {
      try {
        worker = createHashWorker();
        worker.onerror = () => {};
        workerRef.current = worker;
      } catch {
        worker = null;
      }
    }

    // Tracked separately from job status: a state update from updateJob() isn't
    // guaranteed to have flushed into jobsRef.current by the time the loop next
    // checks — file.arrayBuffer() can resolve within the same microtask tick,
    // faster than React re-renders. Without this, the loop can re-select the
    // same "queued" job forever, each pass creating a fresh worker listener and
    // never releasing the last one — unbounded growth, not just a slow bug.
    const claimed = new Set();

    while (!cancelRef.current) {
      const next = jobsRef.current.find(j => j.status === 'queued' && !claimed.has(j.id));
      if (!next) break;
      claimed.add(next.id);

      setCurrentId(next.id);
      setHashProg(0);

      try {
        updateJob(next.id, { status: 'hashing', error: '' });
        const bytes = new Uint8Array(await next.file.arrayBuffer());
        if (cancelRef.current) break;

        if (!worker) throw new Error('Web Workers are unavailable in this browser — hashing needs one.');

        const results = await hashOne(worker, bytes, next.id);
        if (cancelRef.current) break;

        updateJob(next.id, { status: 'done', hashes: results });
      } catch (err) {
        if (cancelRef.current) break;
        updateJob(next.id, { status: 'error', error: err?.message || String(err) });
      }
    }

    runningRef.current = false;
    setRunning(false);
    setCurrentId(null);
    setHashProg(0);
  }, [updateJob, hashOne, algorithms]);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    rejectRef.current?.(new Error('Cancelled'));
    rejectRef.current = null;
    workerRef.current?.terminate();
    workerRef.current = null;

    runningRef.current = false;
    setRunning(false);
    setCurrentId(null);
    setHashProg(0);
    setJobs(prev => prev.map(j => (j.status === 'hashing' ? { ...j, status: 'queued' } : j)));
  }, []);

  const requeue = useCallback((id) => {
    updateJob(id, { status: 'queued', error: '', hashes: {} });
  }, [updateJob]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1600);
    } catch { /* clipboard blocked — nothing else to fall back to for a single hash */ }
  };

  // Which computed hash (if any) a pasted "expected" value should be checked
  // against — prefer the algorithm whose digest length matches what was
  // pasted, so a SHA-1 paste doesn't get silently compared to a SHA-256 hash.
  const matchFor = (job) => {
    if (!job.expectedHash?.trim() || !Object.keys(job.hashes).length) return null;
    const guessed = algorithmForHashLength(job.expectedHash);
    const candidates = guessed && job.hashes[guessed] ? [job.hashes[guessed]] : Object.values(job.hashes);
    return candidates.some(h => hashesMatch(h, job.expectedHash));
  };

  const reportJobs = () => doneJobs.map(j => ({
    name: j.name, size: j.size, hashes: j.hashes,
    expectedHash: j.expectedHash?.trim() || '', verifiedAgainst: matchFor(j),
  }));

  const exportLog = () => download('hash-log.txt', buildHashLogText(reportJobs()), 'text/plain');

  const exportPdf = useCallback(async () => {
    if (!doneJobs.length) return;
    setExporting(true);
    setExportError('');
    try {
      const { default: pdfMake } = await import('pdfmake/build/pdfmake');
      const pdfFonts = await import('pdfmake/build/vfs_fonts');
      pdfMake.vfs = pdfFonts.default || pdfFonts;
      pdfMake.createPdf(buildHashReportDoc(reportJobs())).download('hash-report.pdf');
    } catch (err) {
      setExportError(err?.message || 'Could not build the PDF report.');
    } finally {
      setExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneJobs]);

  const currentJob = jobs.find(j => j.id === currentId);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#0f172a',
    }}>
      <Navbar pageTitle="File Hash / Chain-of-Custody Verifier" />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 72px' }}>

        {/* Privacy explainer */}
        <div style={{
          background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12,
          padding: '14px 18px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: 20, lineHeight: 1 }}>🔒</div>
          <div style={{ fontSize: 13, color: '#065f46', lineHeight: 1.6 }}>
            <strong>Your files never leave this computer.</strong> Hashing happens entirely in your
            browser tab using the same cryptographic functions built into it — nothing is uploaded,
            and this tool makes no network requests of any kind.
          </div>
        </div>

        {/* What this is for */}
        <div style={{
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
          padding: '14px 18px', marginBottom: 20, fontSize: 12.5, color: '#475569', lineHeight: 1.65,
        }}>
          <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: 13 }}>
            What this is for
          </div>
          Hash a file before and after it moves — copied to another drive, sent to a lab, attached
          to a report — to prove it wasn&rsquo;t altered. Paste a previously recorded hash into a
          file&rsquo;s <strong>expected hash</strong> field to check it instantly, or export a hash
          log to attach to your case file as chain-of-custody documentation.
        </div>

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#6366f1' : '#cbd5e1'}`,
            borderRadius: 12,
            padding: jobs.length ? '22px 24px' : '40px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? '#eef2ff' : '#fff',
            transition: 'border-color .15s, background .15s',
            marginBottom: 20,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <div style={{ fontSize: jobs.length ? 26 : 36, marginBottom: 8 }}>{jobs.length ? '🔑' : '🧾'}</div>
          <div style={{ fontWeight: 700, fontSize: jobs.length ? 14 : 16, color: '#0f172a', marginBottom: 4 }}>
            {jobs.length ? 'Add more files' : 'Drop any file here, or click to browse'}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            Any file type — evidence images, videos, documents, disk images · several files at once
          </div>
        </div>

        {sizeError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10,
            padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#b91c1c', lineHeight: 1.6,
          }}>
            {sizeError}
          </div>
        )}

        {/* Algorithms + run */}
        {jobs.length > 0 && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
              Algorithms to compute
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
              {ALGORITHMS.map(a => (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: running ? 'default' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={algorithms.includes(a.id)}
                    disabled={running}
                    onChange={() => toggleAlgorithm(a.id)}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {a.label}{a.recommended && <span style={{ color: '#94a3b8', fontWeight: 500 }}> (recommended)</span>}
                  </span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
              SHA-256 is the modern standard. MD5 and SHA-1 are offered for matching against hash
              values already on record elsewhere (a lot of legacy forensic tooling defaults to
              them) — not recommended on their own, since both are broken for collision resistance.
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                onClick={runQueue}
                disabled={running || !queuedCount || !algorithms.length}
                style={{
                  background: running || !queuedCount || !algorithms.length ? '#c7d2fe' : '#4f46e5',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 22px', fontSize: 14, fontWeight: 700,
                  cursor: running || !queuedCount || !algorithms.length ? 'default' : 'pointer',
                }}
              >
                {running ? 'Working…' : queuedCount > 1 ? `Hash ${queuedCount} files` : 'Compute hash'}
              </button>
              {running && (
                <button onClick={cancel} style={{ ...smallButton, padding: '9px 18px', fontSize: 13 }}>
                  Cancel
                </button>
              )}
              {!algorithms.length && (
                <span style={{ fontSize: 12, color: '#b45309' }}>Select at least one algorithm.</span>
              )}
            </div>
          </div>
        )}

        {/* Progress */}
        {running && currentJob && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '16px 20px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentJob.name}
            </div>
            <Bar pct={hashProg * 100} />
          </div>
        )}

        {/* File queue */}
        {jobs.length > 0 && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
              borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>
                {jobs.length} file{jobs.length !== 1 ? 's' : ''}
                <span style={{ fontWeight: 500, color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>
                  {doneJobs.length} done{queuedCount ? ` · ${queuedCount} queued` : ''}
                </span>
              </div>
              {doneJobs.length > 0 && (
                <>
                  <button onClick={exportLog} style={smallButton}>Hash log (.txt)</button>
                  <button onClick={exportPdf} disabled={exporting} style={smallButton}>
                    {exporting ? 'Building…' : 'PDF report'}
                  </button>
                </>
              )}
              <button onClick={clearAll} disabled={running} style={{ ...smallButton, opacity: running ? 0.5 : 1 }}>
                Clear all
              </button>
            </div>

            {exportError && (
              <div style={{ padding: '8px 18px', fontSize: 12, color: '#b91c1c', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                {exportError}
              </div>
            )}

            {jobs.map((job) => {
              const match = matchFor(job);
              return (
                <div key={job.id} style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmtBytes(job.size)}</div>
                      {job.error && (
                        <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 4, lineHeight: 1.5 }}>{job.error}</div>
                      )}
                    </div>
                    <StatusChip status={job.status} />
                    {job.status === 'error' && (
                      <button onClick={() => requeue(job.id)} style={{ ...smallButton, padding: '3px 9px' }}>Retry</button>
                    )}
                    <button
                      onClick={() => removeJob(job.id)}
                      disabled={running && job.id === currentId}
                      title={`Remove ${job.name}`}
                      aria-label={`Remove ${job.name}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: '0 4px' }}
                    >
                      ×
                    </button>
                  </div>

                  {job.status === 'done' && (
                    <div style={{ marginTop: 10 }}>
                      {Object.entries(job.hashes).map(([algo, hash]) => (
                        <div key={algo} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', width: 62, flexShrink: 0 }}>
                            {ALGORITHMS.find(a => a.id === algo)?.label || algo}
                          </span>
                          <code style={{
                            flex: 1, fontSize: 12, background: '#f8fafc', border: '1px solid #e2e8f0',
                            borderRadius: 6, padding: '4px 8px', overflowX: 'auto', whiteSpace: 'nowrap',
                          }}>
                            {hash}
                          </code>
                          <button onClick={() => copy(hash, `${job.id}-${algo}`)} style={{ ...smallButton, padding: '3px 8px', flexShrink: 0 }}>
                            {copied === `${job.id}-${algo}` ? 'Copied ✓' : 'Copy'}
                          </button>
                        </div>
                      ))}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <input
                          value={job.expectedHash}
                          onChange={(e) => setExpectedHash(job.id, e.target.value)}
                          placeholder="Paste a known hash to verify against…"
                          aria-label={`Expected hash for ${job.name}`}
                          style={{
                            flex: 1, fontSize: 12, padding: '6px 9px', border: '1px solid #cbd5e1',
                            borderRadius: 6, fontFamily: 'monospace',
                          }}
                        />
                        {job.expectedHash.trim() && (
                          <span style={{
                            fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                            color: match ? '#15803d' : '#b91c1c',
                            background: match ? '#dcfce7' : '#fee2e2',
                            flexShrink: 0,
                          }}>
                            {match ? '✓ Match' : '✗ Mismatch'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!jobs.length && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, marginTop: 40, lineHeight: 1.7 }}>
            Drop in one or more files to compute cryptographic hashes for chain-of-custody
            documentation.
          </div>
        )}
      </div>
    </div>
  );
}
